import type {
  DocumentSectionSpec,
  DocumentTemplate,
  TemplateIssue,
} from '@/entities/templates';

import { IDLE_TEMPLATE_EDIT } from '../config';

import {
  copyTemplateForEncounter,
  isStructureChanged,
  type TemplateDraft,
} from './templateDraft';

type TemplateSectionPatch = Partial<
  Pick<
    DocumentSectionSpec,
    'Title' | 'Instructions' | 'Required' | 'Code' | 'AllowAITextRewrite'
  >
>;

type TemplateLineChange = {
  kind:
    | 'addLine'
    | 'removeLine'
    | 'moveLine'
    | 'changeLinks'
    | 'addBlock'
    | 'changeBlock'
    | 'removeBlock';
  section: string;
  line: string;
};

// One change of an edit, named the way the template read when it was made,
// so the clinician can make it again by hand on another version. A line is
// named by its wording, and a field list's sections are its fields. The text
// the clinician typed is kept whole: an instruction, a wording, an added
// line's wording, and the hint an empty value block shows.
export type TemplateChangeSummary =
  | { kind: 'renameSection'; from: string; to: string }
  | { kind: 'changeInstructions'; section: string; to: string }
  | {
      kind: 'addSection' | 'removeSection' | 'moveSection';
      section: string;
    }
  | { kind: 'setRequired'; section: string; required: boolean }
  | { kind: 'setRewrite'; section: string; allowed: boolean }
  | TemplateLineChange
  | { kind: 'changeWording'; section: string; from: string; to: string }
  | {
      kind: 'changeHint';
      section: string;
      line: string;
      from: string;
      to: string;
    }
  | {
      kind: 'addOption' | 'removeOption' | 'moveOption';
      section: string;
      option: string;
    }
  | { kind: 'renameOption'; section: string; from: string; to: string };

export const describeSectionUpdate = (
  section: DocumentSectionSpec,
  patch: TemplateSectionPatch,
): TemplateChangeSummary[] => {
  const title = patch.Title ?? section.Title;
  return [
    ...(patch.Title !== undefined && patch.Title !== section.Title
      ? [{ kind: 'renameSection' as const, from: section.Title, to: title }]
      : []),
    ...('Instructions' in patch &&
    (patch.Instructions ?? '') !== (section.Instructions ?? '')
      ? [
          {
            kind: 'changeInstructions' as const,
            section: title,
            to: (patch.Instructions ?? '').trim(),
          },
        ]
      : []),
    ...(patch.Required !== undefined && patch.Required !== section.Required
      ? [
          {
            kind: 'setRequired' as const,
            section: title,
            required: patch.Required,
          },
        ]
      : []),
    ...(patch.AllowAITextRewrite !== undefined &&
    patch.AllowAITextRewrite !== !!section.AllowAITextRewrite
      ? [
          {
            kind: 'setRewrite' as const,
            section: title,
            allowed: patch.AllowAITextRewrite,
          },
        ]
      : []),
  ];
};

// A template's structure edited where it is read and saved later as a
// clinic version. The draft is kept after "Done" while it holds changes;
// `source` is the version the draft was made from, which a save is based
// on. The template's document keeps its own history and writes the whole
// draft back.
export type TemplateEditState = {
  editing: boolean;
  source: DocumentTemplate | null;
  draft: TemplateDraft | null;
  // What scribe refused in the last save, marked where it is.
  issues: TemplateIssue[];
  // What a block template's document cannot save as it is.
  documentIssues: TemplateIssue[];
};

export const holdsTemplateEditChanges = ({
  draft,
  source,
}: TemplateEditState) =>
  !!draft && !!source && isStructureChanged({ draft, source });

// An edit that holds a draft goes on with it; a new one copies the source.
export const startTemplateEdit = (
  state: TemplateEditState,
  source: DocumentTemplate,
): TemplateEditState =>
  state.draft && state.source
    ? { ...state, editing: true }
    : {
        ...IDLE_TEMPLATE_EDIT,
        editing: true,
        source,
        draft: copyTemplateForEncounter(source),
      };

// The draft as the template's document read it last.
export const replaceTemplateEditDraft = (
  state: TemplateEditState,
  template: DocumentTemplate,
): TemplateEditState =>
  state.draft ? { ...state, draft: { ...state.draft, template } } : state;

// "Done": the edit mode closes, and an edit without changes ends, unless it
// is made on another version than the one `shown`, such as the current
// version a reload started it on: that version stays its source until the
// edit is saved or discarded.
export const finishTemplateEdit = (
  state: TemplateEditState,
  shown?: DocumentTemplate,
): TemplateEditState =>
  holdsTemplateEditChanges(state) ||
  (!!shown && !!state.source && state.source.id !== shown.id)
    ? { ...state, editing: false }
    : IDLE_TEMPLATE_EDIT;

// The edit started again on the series' current version, after a save was
// refused because another version had replaced its source. Nothing of the
// draft is carried over: its changes are listed for the clinician to make
// again by hand.
export const restartTemplateEdit = (
  state: TemplateEditState,
  current: DocumentTemplate,
): TemplateEditState => ({
  ...IDLE_TEMPLATE_EDIT,
  editing: state.editing,
  source: current,
  draft: copyTemplateForEncounter(current),
});

// Another version offered as the source. An edit that holds changes keeps
// the version it was made from, so its save is refused once the series has
// moved on; one without changes starts over on the new version.
export const followTemplateEditSource = (
  state: TemplateEditState,
  source: DocumentTemplate | undefined,
): TemplateEditState => {
  if (state.source?.id === source?.id || holdsTemplateEditChanges(state)) {
    return state;
  }
  return state.editing && source
    ? startTemplateEdit(IDLE_TEMPLATE_EDIT, source)
    : IDLE_TEMPLATE_EDIT;
};
